import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon.jsx';
import { APP_CONFIG } from '../config/appConfig.js';
import { validateInputs, calculateResults } from '../utils/calculations.js';

const { FiCalculator, FiAlertCircle, FiAlertTriangle, FiRotateCcw, FiPlay } = FiIcons;

const Calculator = ({ onResults }) => {
  const [inputs, setInputs] = useState({
    litres: '',
    pricePerLitre: '',
    hours: '',
    avgLoad: ''
  });

  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [isCalculating, setIsCalculating] = useState(false);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
    if (warnings.length > 0) {
      setWarnings([]);
    }
  };

  const handleCalculate = () => {
    setIsCalculating(true);
    
    // Convert strings to numbers
    const numericInputs = {
      litres: parseFloat(inputs.litres) || 0,
      pricePerLitre: parseFloat(inputs.pricePerLitre) || 0,
      hours: parseFloat(inputs.hours) || 0,
      avgLoad: parseFloat(inputs.avgLoad) || 0
    };

    const validation = validateInputs(numericInputs);
    setErrors(validation.errors);
    setWarnings(validation.warnings);

    if (validation.errors.length === 0) {
      const results = calculateResults(numericInputs);
      onResults(numericInputs, results);
    }
    
    setIsCalculating(false);
  };

  const handleReset = () => {
    setInputs({
      litres: '',
      pricePerLitre: '',
      hours: '',
      avgLoad: ''
    });
    setErrors([]);
    setWarnings([]);
    onResults(null, null);
  };

  const loadExample = () => {
    const example = APP_CONFIG.exampleValues;
    setInputs({
      litres: example.litres.toString(),
      pricePerLitre: example.pricePerLitre.toString(),
      hours: example.hours.toString(),
      avgLoad: example.avgLoad.toString()
    });
    setErrors([]);
    setWarnings([]);
  };

  return (
    <section id="calculator" className="py-16 px-4 bg-gray-50">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-xl shadow-lg p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <SafeIcon 
              icon={FiCalculator} 
              className="text-2xl text-violet-600" 
              aria-hidden="true"
            />
            <h2 className="text-2xl font-bold text-gray-900">
              Calculate Your Costs
            </h2>
          </div>

          <div className="space-y-6">
            {/* Monthly Litres */}
            <div>
              <label 
                htmlFor="litres"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Monthly diesel used (litres) *
              </label>
              <input
                type="number"
                id="litres"
                value={inputs.litres}
                onChange={(e) => handleInputChange('litres', e.target.value)}
                placeholder="e.g. 1000"
                min="0"
                step="0.1"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-colors"
                aria-describedby="litres-help"
                required
              />
              <p id="litres-help" className="mt-1 text-sm text-gray-500">
                Total litres of diesel consumed by your generator per month
              </p>
            </div>

            {/* Price per Litre */}
            <div>
              <label 
                htmlFor="pricePerLitre"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Diesel price per litre ({APP_CONFIG.currency.code}) *
              </label>
              <input
                type="number"
                id="pricePerLitre"
                value={inputs.pricePerLitre}
                onChange={(e) => handleInputChange('pricePerLitre', e.target.value)}
                placeholder="e.g. 1.20"
                min="0"
                step="0.01"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-colors"
                aria-describedby="price-help"
                required
              />
              <p id="price-help" className="mt-1 text-sm text-gray-500">
                Current price you pay for diesel per litre
              </p>
            </div>

            {/* Generator Hours */}
            <div>
              <label 
                htmlFor="hours"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Generator hours per month *
              </label>
              <input
                type="number"
                id="hours"
                value={inputs.hours}
                onChange={(e) => handleInputChange('hours', e.target.value)}
                placeholder="e.g. 120"
                min="0"
                max="744"
                step="0.1"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-colors"
                aria-describedby="hours-help"
                required
              />
              <p id="hours-help" className="mt-1 text-sm text-gray-500">
                Total hours your generator runs per month (max 744 hours)
              </p>
            </div>

            {/* Average Load */}
            <div>
              <label 
                htmlFor="avgLoad"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Average load during generator use (kW) *
              </label>
              <input
                type="number"
                id="avgLoad"
                value={inputs.avgLoad}
                onChange={(e) => handleInputChange('avgLoad', e.target.value)}
                placeholder="e.g. 10"
                min="0"
                step="0.1"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-colors"
                aria-describedby="load-help"
                required
              />
              <p id="load-help" className="mt-1 text-sm text-gray-500">
                Average electrical load (power draw) when generator is running
              </p>
            </div>

            {/* Error Messages */}
            {errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <SafeIcon 
                    icon={FiAlertCircle} 
                    className="text-red-500 mt-0.5 flex-shrink-0" 
                    aria-hidden="true"
                  />
                  <div>
                    <h4 className="font-medium text-red-800 mb-1">Please fix these errors:</h4>
                    <ul className="text-sm text-red-700 space-y-1">
                      {errors.map((error, index) => (
                        <li key={index}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Warning Messages */}
            {warnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <SafeIcon 
                    icon={FiAlertTriangle} 
                    className="text-amber-500 mt-0.5 flex-shrink-0" 
                    aria-hidden="true"
                  />
                  <div>
                    <h4 className="font-medium text-amber-800 mb-1">Warnings:</h4>
                    <ul className="text-sm text-amber-700 space-y-1">
                      {warnings.map((warning, index) => (
                        <li key={index}>• {warning}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <motion.button
                onClick={handleCalculate}
                disabled={isCalculating}
                className="flex-1 bg-violet-600 hover:bg-violet-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-medium transition-colors focus:outline-none focus:ring-4 focus:ring-violet-200 flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiCalculator} aria-hidden="true" />
                {isCalculating ? 'Calculating...' : 'Calculate'}
              </motion.button>

              <motion.button
                onClick={loadExample}
                className="px-6 py-3 border border-violet-600 text-violet-600 hover:bg-violet-50 rounded-lg font-medium transition-colors focus:outline-none focus:ring-4 focus:ring-violet-200 flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiPlay} aria-hidden="true" />
                Try Example
              </motion.button>

              <motion.button
                onClick={handleReset}
                className="px-6 py-3 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg font-medium transition-colors focus:outline-none focus:ring-4 focus:ring-gray-200 flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiRotateCcw} aria-hidden="true" />
                Reset
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Calculator;